import {
  getMetadataStorage,
  IS_ARRAY,
  IS_BOOLEAN,
  IS_DATE,
  IS_ENUM,
  IS_IN,
  IS_INT,
  IS_NUMBER,
  IS_OBJECT,
  IS_STRING,
  IS_UUID,
  ValidationTypes,
} from 'class-validator';
import { FilterOperation } from '../filter/filter-criteria';
import { assertBackendFieldNamesAreUnique } from './backend-field-names';
import { IrdPropertyType } from './property';

/**
 * What the API permits for a field, as the backend's field annotations state it.
 *
 * A capability left undefined means **unknown**, not forbidden: until the specification carries
 * it, behaviour stays exactly what it is today.
 */
export interface IrdApiCapabilities {
  sortable?: boolean;
  filterOperations?: FilterOperation[];
  searchable?: boolean;
  /**
   * Whether the API accepts a write to the field; `false` where the specification marks it
   * `readOnly`. A bound, not an intent: no form or table declaration can open up a field the API
   * refuses. The legacy edit dialog reads only `IrdProperty.editable`, so it does not see this bound yet.
   */
  editable?: boolean;
}

/** The schema facts of one field, read off a decorated class. */
export interface IrdFieldFacts<T> {
  name: keyof T & string;
  type: IrdPropertyType;
  /** Present only on a field holding a list, whose `type` is then the type of each element. */
  array?: true;
  daoName?: string;
  capabilities?: IrdApiCapabilities;
}

interface FieldDeclaration {
  daoName?: string;
  capabilities?: IrdApiCapabilities;
}

// The class-validator decorators that carry a field's type. Written into the decorator rather than
// read from `design:type`, because `emitDecoratorMetadata` is off on the Angular side. `@Allow()`,
// which has no name, marks a value of any shape and is read as 'json' — but only on a field no type
// decorator names, since it is also class-validator's plain whitelist marker beside one.
const PROPERTY_TYPE_BY_VALIDATOR: Record<string, IrdPropertyType> = {
  [IS_STRING]: 'string',
  [IS_NUMBER]: 'number',
  [IS_INT]: 'number',
  [IS_BOOLEAN]: 'boolean',
  [IS_DATE]: 'Date',
  [IS_ENUM]: 'enum',
  [IS_IN]: 'enum',
  [IS_OBJECT]: 'Object',
  [IS_UUID]: 'string',
};

// Our half of the facts — what class-validator has no concept of. Keyed by class, so a class that
// is never derived never holds anything here beyond its own lifetime.
// ponytail: own fields only, no inheritance — add a prototype walk once a generated class extends another.
const declarations = new WeakMap<object, Map<string, FieldDeclaration>>();

function declare(target: object, propertyKey: string | symbol, declaration: FieldDeclaration): void {
  const fields = declarations.get(target.constructor) ?? new Map<string, FieldDeclaration>();
  const name = propertyKey.toString();
  fields.set(name, { ...fields.get(name), ...declaration });
  declarations.set(target.constructor, fields);
}

/** Declares the name the backend knows this field by, where it differs from the field's own name. */
export function DaoName(daoName: string): PropertyDecorator {
  return (target, propertyKey) => declare(target, propertyKey, { daoName });
}

/** Declares what the API permits for this field. */
export function ApiCapabilities(capabilities: IrdApiCapabilities): PropertyDecorator {
  return (target, propertyKey) => declare(target, propertyKey, { capabilities });
}

/**
 * Reads an entity's field facts off its decorated class: the field list and each field's type
 * from the class-validator decorators, the backend name and the capabilities from ours.
 *
 * The field list comes from decorator metadata and never from an instance. With
 * `useDefineForClassFields: false` a declared-but-uninitialised field emits nothing, so
 * `new Entity()` has no own properties to list.
 *
 * Throws rather than returning something quietly wrong: on a class with no decorated field, which
 * would otherwise map every object to an empty one; on a field with no type, or more than one; and
 * on two fields claiming the same backend field.
 */
export function deriveFieldFacts<T>(entity: new (...args: never[]) => T): IrdFieldFacts<T>[] {
  const { typesByField, arrayFields, declaredArrays, anyShapeFields } = readValidations(entity);
  typeAnyShapeFields(typesByField, arrayFields, anyShapeFields);
  assertArraysDeclareElementType(entity, declaredArrays, arrayFields);

  const declared = declarations.get(entity) ?? new Map<string, FieldDeclaration>();
  const names = new Set([...typesByField.keys(), ...declared.keys()]);
  if (names.size === 0) {
    throw new Error(`'${entity.name}' declares no decorated field, so no field facts can be derived from it`);
  }

  const facts = [...names].map((name) => {
    const types = [...(typesByField.get(name) ?? [])];
    if (types.length !== 1) {
      throw new Error(
        `Field '${entity.name}.${name}' must declare exactly one type through a class-validator decorator, ` +
          `but declares ${types.length === 0 ? 'none' : types.join(', ')}`,
      );
    }
    return {
      name: name as keyof T & string,
      type: types[0],
      ...(arrayFields.has(name) && { array: true as const }),
      ...declared.get(name),
    };
  });

  assertBackendFieldNamesAreUnique(facts);
  return facts;
}

/** Reads each field's types off its class-validator decorators, and which fields hold lists. */
function readValidations(entity: new (...args: never[]) => unknown) {
  const typesByField = new Map<string, Set<IrdPropertyType>>();
  const arrayFields = new Set<string>();
  const declaredArrays = new Set<string>();
  const anyShapeFields = new Map<string, boolean>();
  for (const validation of getMetadataStorage().getTargetValidationMetadatas(entity, '', true, false)) {
    const types = typesByField.get(validation.propertyName) ?? new Set<IrdPropertyType>();
    if (validation.type === ValidationTypes.WHITELIST) {
      anyShapeFields.set(validation.propertyName, !!validation.each);
    }
    const type = PROPERTY_TYPE_BY_VALIDATOR[validation.name ?? ''];
    if (type) {
      types.add(type);
      // A type decorator applied to each element is what makes the field a list of that type.
      if (validation.each) {
        arrayFields.add(validation.propertyName);
      }
    }
    if (validation.name === IS_ARRAY) {
      declaredArrays.add(validation.propertyName);
    }
    typesByField.set(validation.propertyName, types);
  }
  return { typesByField, arrayFields, declaredArrays, anyShapeFields };
}

/** Types an `@Allow()` field as 'json', unless a type decorator already names its type. */
function typeAnyShapeFields(
  typesByField: Map<string, Set<IrdPropertyType>>,
  arrayFields: Set<string>,
  anyShapeFields: Map<string, boolean>,
): void {
  for (const [name, each] of anyShapeFields) {
    const types = typesByField.get(name) ?? new Set<IrdPropertyType>();
    if (types.size === 0) {
      types.add('json');
      if (each) {
        arrayFields.add(name);
      }
    }
    typesByField.set(name, types);
  }
}

function assertArraysDeclareElementType(entity: { name: string }, declaredArrays: Set<string>, arrayFields: Set<string>): void {
  for (const name of declaredArrays) {
    if (!arrayFields.has(name)) {
      throw new Error(`Field '${entity.name}.${name}' is declared an array without the type of its elements ({ each: true })`);
    }
  }
}
